import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@design-system/Button';
import { NavBar } from '@design-system/NavBar';
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@design-system/Dialog';
import { SMPDialogContent } from './components';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { Skeleton } from '@design-system/Skeleton';
import { useSMPData } from './hooks/useSMPData';
import { useMaintenanceGuard } from './hooks/useMaintenanceGuard';
import {
  useGetDeliveryDate,
  useGetOrderStatusLazy,
  useOrderNow,
} from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import { SMP_PATH } from '@/constants/paths';

type PageView = 'default' | 'order-in-progress' | 'success';

export const SMPOrderNowPage = () => {
  const navigate = useNavigate();
  const {
    subscriptionId,
    subscriptionDetail,
    showMaintenance,
    isSessionReady,
    isLoading: isSMPLoading,
    isError: isSMPError,
  } = useSMPData({
    skip: { discounts: true, changePlan: true, uiMessages: true, canSkip: true, holdingPattern: true },
  });

  useMaintenanceGuard(showMaintenance);

  const {
    data: deliveryDateData,
    isLoading: isLoadingDeliveryDate,
  } = useGetDeliveryDate(subscriptionId, {
    enabled: isSessionReady && !!subscriptionId,
  });

  const {
    refetch: fetchOrderStatus,
    isFetching: isFetchingOrderStatus,
  } = useGetOrderStatusLazy(subscriptionId);

  const orderNowMutation = useOrderNow();

  const [view, setView] = useState<PageView>('default');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isLoading = isSMPLoading || isLoadingDeliveryDate;
  const goBack = () => navigate(SMP_PATH);

  if (isLoading) {
    return (
      <section className="page-container flex flex-col gap-12 pb-24">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-12 w-full max-w-sm rounded-lg" />
      </section>
    );
  }

  if (isSMPError || !subscriptionDetail) {
    return <ErrorLoadingPage />;
  }

  const estimatedDeliveryDate =
    deliveryDateData?.estimatedDeliveryDate || 'in a few days';

  const handleConfirm = async () => {
    setErrorMessage(null);
    try {
      const { data: statusData } = await fetchOrderStatus();
      if (statusData?.isProcessing) {
        setView('order-in-progress');
        return;
      }
      placeOrder();
    } catch {
      placeOrder();
    }
  };

  const handleOrderNowFromPending = () => {
    setErrorMessage(null);
    placeOrder();
  };

  const placeOrder = () => {
    orderNowMutation.mutate(subscriptionId, {
      onSuccess: (data: unknown) => {
        const responseData = data as { data?: { data?: { error?: string } } };
        if (responseData?.data?.data?.error) {
          setErrorMessage(responseData.data.data.error);
          return;
        }
        setView('success');
      },
      onError: (error: Error) => {
        setErrorMessage(
          error.message || 'An error occurred while processing your order.',
        );
      },
    });
  };

  const handleSuccess = () => {
    navigate(SMP_PATH, { replace: true });
  };

  const handleErrorClose = () => {
    setErrorMessage(null);
    goBack();
  };

  const isConfirming = isFetchingOrderStatus || orderNowMutation.isPending;

  return (
    <section
      className="page-container flex flex-col gap-12 pb-24"
      data-testid="smp-orderNow-page"
    >
      <div className="flex flex-col gap-6">
        <NavBar
          returnButtonTitle="Back"
          className="md:-ml-9"
          onReturnClick={goBack}
        />
      </div>

      {view === 'default' && (
        <DefaultView
          estimatedDeliveryDate={estimatedDeliveryDate}
          isConfirming={isConfirming}
          onConfirm={handleConfirm}
          onCancel={goBack}
        />
      )}

      {view === 'order-in-progress' && (
        <OrderInProgressView
          isConfirming={isConfirming}
          onConfirm={handleOrderNowFromPending}
          onCancel={goBack}
        />
      )}

      {view === 'success' && (
        <SuccessView onOk={handleSuccess} />
      )}

      <Dialog
        open={!!errorMessage}
        onOpenChange={(isOpen) => !isOpen && handleErrorClose()}
      >
        <SMPDialogContent>
          <DialogHeader className="gap-6 text-start">
            <DialogTitle className="typography-h3 text-primary">
              Error
            </DialogTitle>
            <DialogDescription className="typography-body text-secondary">
              {errorMessage}
            </DialogDescription>
          </DialogHeader>
        </SMPDialogContent>
      </Dialog>
    </section>
  );
};

const DefaultView = ({
  estimatedDeliveryDate,
  isConfirming,
  onConfirm,
  onCancel,
}: {
  estimatedDeliveryDate: string;
  isConfirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <div className="w-full max-w-lg flex flex-col gap-10">
    <div className="flex flex-col gap-8">
      <h1
        className="text-[20px] md:text-[40px] font-medium md:font-normal text-primary"
        style={{ lineHeight: '115%', letterSpacing: '0%' }}
        data-testid="smp-orderNow-title"
      >
        Ordering now will charge you immediately for your next order.
      </h1>
      <p
        className="typography-body text-secondary"
        data-testid="smp-orderNow-deliveryDate"
      >
        Manufacturing will begin as soon as possible, and your order estimated
        delivery date is {estimatedDeliveryDate}.
      </p>
      <p className="typography-body text-secondary">
        Future orders will resume on a monthly cadence.
      </p>
    </div>

    <div className="flex flex-col gap-4 w-full max-w-sm">
      <Button
        onClick={onConfirm}
        disabled={isConfirming}
        data-testid="smp-orderNow-confirm"
      >
        {isConfirming ? 'Processing...' : 'Confirm Order'}
      </Button>
      <Button
        variant="secondary"
        onClick={onCancel}
        disabled={isConfirming}
        data-testid="smp-orderNow-cancel"
      >
        Cancel
      </Button>
    </div>
  </div>
);

const OrderInProgressView = ({
  isConfirming,
  onConfirm,
  onCancel,
}: {
  isConfirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <div className="w-full max-w-lg flex flex-col gap-10">
    <div className="flex flex-col gap-8">
      <h1
        className="text-[20px] md:text-[40px] font-medium md:font-normal text-primary"
        style={{ lineHeight: '115%', letterSpacing: '0%' }}
        data-testid="smp-orderNow-inProgressTitle"
      >
        It looks like you already have a pending new order.
      </h1>
      <p className="typography-body text-secondary">
        Would you still like to place another order?
      </p>
    </div>

    <div className="flex flex-col gap-4 w-full max-w-sm">
      <Button
        onClick={onConfirm}
        disabled={isConfirming}
        data-testid="smp-orderNow-orderNowBtn"
      >
        {isConfirming ? 'Processing...' : 'Order Now'}
      </Button>
      <Button
        variant="secondary"
        onClick={onCancel}
        disabled={isConfirming}
        data-testid="smp-orderNow-cancelRequest"
      >
        Cancel Request
      </Button>
    </div>
  </div>
);

const SuccessView = ({
  onOk,
}: {
  onOk: () => void;
}) => (
  <div className="w-full max-w-lg flex flex-col gap-10">
    <div className="flex flex-col gap-8">
      <h1
        className="text-[20px] md:text-[40px] font-medium md:font-normal text-primary"
        style={{ lineHeight: '115%', letterSpacing: '0%' }}
        data-testid="smp-orderNow-successTitle"
      >
        Your order is confirmed!
      </h1>
      <p className="typography-body text-secondary">
        Note that it may take up to 1 hour to receive your order confirmation
        email.
      </p>
    </div>

    <div className="flex flex-col gap-4 w-full max-w-sm">
      <Button
        onClick={onOk}
        data-testid="smp-orderNow-ok"
      >
        Return to My Account
      </Button>
    </div>
  </div>
);
